import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { setAuthToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      if (data?.token) setAuthToken(data.token);
      await refresh?.();
      navigate("/inbox");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't reset password. The link may have expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafb] flex flex-col p-10 lg:p-16">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-[#0033CC] flex items-center justify-center rounded-sm">
          <Mail className="w-4 h-4 text-white" strokeWidth={2} />
        </div>
        <span className="font-heading text-lg font-medium tracking-tight">InboxHQ</span>
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-sm w-full" data-testid="reset-password-page">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">Reset password</p>
          <h1 className="font-heading text-3xl font-medium tracking-tight mb-2">Choose a new password</h1>
          <p className="text-sm text-zinc-600 mb-8 leading-relaxed">
            Must be at least 8 characters. You'll be signed in automatically.
          </p>

          {!token && (
            <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-md mb-4">
              No reset token provided. Open the link from your email or <Link to="/forgot-password" className="underline">request a new one</Link>.
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <Label htmlFor="new-password" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                required minLength={8}
                autoFocus
                data-testid="reset-new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                required minLength={8}
                data-testid="reset-confirm-password-input"
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-md" data-testid="reset-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={submitting || !token} className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium" data-testid="reset-submit-button">
              {submitting ? "Updating…" : "Update password & sign in"}
            </Button>

            <Link to="/login" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 justify-center">
              <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Back to sign in
            </Link>
          </form>
        </div>
      </div>

      <div className="text-xs text-zinc-400">© 2026 Internal Inbox — Shared inbox infrastructure.</div>
    </div>
  );
}
